import React, { FC, useEffect, useState } from "react"
import { Alert, Button, Card, Col, Input, Radio, Row, Space, Spin, Tag, Typography, message } from "antd"
import { PlusCircleOutlined } from "@ant-design/icons"
import { blue } from "@ant-design/colors"
import { useHistory, useLocation, useParams } from "react-router-dom"
import { useTranslation } from "react-i18next"
import * as Sentry from "@sentry/react"
import { Booker, useCreateInterestRegistrationMutation, useGetUpdatedBookerQuery } from "../../generated/graphql"
import { URL_BASE, URL_INTEREST_DETAILS, URL_LOGIN, URL_RESULTS } from "../../models/url"
import { margin } from "../../styles/layout"
import { safeStorage } from "../../helpers/safeStorage"
import { safeJsonParse } from "../../helpers/safeJsonParse"
import { parentReportConversion } from "../../helpers/parentIFrame"
import { reportEmbedConversion } from "../../services/reportEndUserEvent"
import { getGraphQLErrorMessage } from "../../helpers/sentry"
import CreateEditAttendee from "../../views/CreateEditAttendee/CreateEditAttendee"
import { useMediaSize } from "../../layouts/Main/GetWindowDimensions/GetWindowDimensions"
import InterestTimeSlotSelect, { TimeSlotSelectionValue } from "./InterestTimeSlotSelect"
import { isValidTimeSlotSelection } from "./timeSlotSelection"
import { useInterestListById } from "./interestListData"
import QueryErrorAlert from "../../components/QueryErrorAlert"
import { persistLoginFromUrl } from "../login/loginFromUrl"

const { Paragraph, Text, Title } = Typography
const NOTE_MAX_LENGTH = 250

// Anmelde-Flow für eine Interessenten-Liste — login-pflichtig wie eine Buchung,
// nur ohne Zahlung. Eingeloggter Booker + gewählter Attendee (Muster aus dem
// Buchungsflow: bestehenden wählen oder neu anlegen). Bestätigungs-Mail macht
// das Backend.
const InterestRegistration: FC = () => {
    const { id } = useParams<{ id: string }>()
    const history = useHistory()
    const location = useLocation()
    const { t } = useTranslation()
    const { media } = useMediaSize()

    const isLoggedIn = !!safeStorage.getItem("loggedInUser")
    const storedBooker = safeJsonParse<Booker>(safeStorage.getItem("booker"))

    // Login-Redirect wie in BookingSummary: Kontext als fromUrl mitgeben.
    useEffect(() => {
        if (!isLoggedIn) {
            persistLoginFromUrl({ pathname: location.pathname })
            history.push(URL_LOGIN)
        }
    }, [isLoggedIn, history, location.pathname])

    const { list, data: listData, loading: loadingList, error: listError, notFound: listNotFound, refetch: refetchList } = useInterestListById(id)

    const { data: bookerData, loading: loadingBooker, refetch: refetchBooker } = useGetUpdatedBookerQuery({
        skip: !storedBooker?.id,
        variables: { id: storedBooker?.id ?? "" },
    })
    const booker = (bookerData?.booker as Booker | undefined) ?? storedBooker ?? undefined

    const [selectedAttendeeId, setSelectedAttendeeId] = useState<string>()
    const [showCreateAttendee, setShowCreateAttendee] = useState(false)
    const [timeSlotValue, setTimeSlotValue] = useState<TimeSlotSelectionValue>({ allTimesPossible: false, selectedTimeSlotIds: [] })
    const [note, setNote] = useState("")
    const [showError, setShowError] = useState(false)

    const [createInterestRegistration, { loading: submitting }] = useCreateInterestRegistrationMutation()

    if (!isLoggedIn) return null

    if (loadingList && !listData) {
        return (
            <div style={{ display: "flex", justifyContent: "center", padding: margin.std }}>
                <Spin />
            </div>
        )
    }

    if (listError && !listData) {
        return (
            <div style={{ margin: margin.s }}>
                <QueryErrorAlert
                    testId='interest-registration-error'
                    message={t("interest.details.loadError", "Die Details zur Interessenten-Liste konnten nicht geladen werden.")}
                    onRetry={() => refetchList()}
                />
            </div>
        )
    }

    if (listNotFound) {
        return (
            <Card style={{ margin: margin.s }}>
                <Space direction='vertical' align='center' style={{ width: "100%" }}>
                    <Title level={4}>{t("interest.notFound.title", "Angebot nicht gefunden")}</Title>
                    <Button type='primary' onClick={() => history.push(URL_BASE)}>
                        {t("resultScreen.backToSearch", "Zurück zur Kurssuche")}
                    </Button>
                </Space>
            </Card>
        )
    }

    if (!list) {
        return (
            <div style={{ display: "flex", justifyContent: "center", padding: margin.std }}>
                <Spin />
            </div>
        )
    }

    if (list.isFull) {
        const detailsUrl = URL_INTEREST_DETAILS.replace(":id", list.id)
        return (
            <Card style={{ margin: margin.s }} data-testid='interest-registration-full'>
                <Space direction='vertical' align='center' style={{ width: "100%" }}>
                    <Alert
                        type='info'
                        showIcon
                        message={t("interest.ctaFull", "Liste ist voll")}
                        description={t(
                            "interest.flow.full",
                            "Diese Interessenten-Liste ist bereits voll. Ein weiterer Eintrag ist nicht möglich."
                        )}
                    />
                    <Button type='primary' onClick={() => history.push(detailsUrl)}>
                        {t("interest.details.breadcrumb", "Details zur Interessenten-Liste")}
                    </Button>
                </Space>
            </Card>
        )
    }

    const hasTimeSlots = (list.timeSlots ?? []).length > 0
    const attendees = booker?.attendees ?? []
    const timeSlotSelectionValid = isValidTimeSlotSelection({
        allTimesPossible: timeSlotValue.allTimesPossible,
        selectedTimeSlotIds: timeSlotValue.selectedTimeSlotIds,
        hasTimeSlots,
    })
    const canSubmit = Boolean(booker?.id) && Boolean(selectedAttendeeId) && timeSlotSelectionValid

    const submit = async () => {
        if (!canSubmit || !booker?.id || !selectedAttendeeId) {
            setShowError(true)
            return
        }
        try {
            const { data: registration } = await createInterestRegistration({
                variables: {
                    input: {
                        interestListId: list.id,
                        bookerId: booker.id,
                        attendeeId: selectedAttendeeId,
                        selectedTimeSlotIds: timeSlotValue.allTimesPossible ? [] : timeSlotValue.selectedTimeSlotIds,
                        allTimesPossible: timeSlotValue.allTimesPossible,
                        note: note.trim() ? note.trim() : undefined,
                    },
                },
            })
            const registrationId = registration?.createInterestRegistration?.id
            if (registrationId) {
                // Conversion an die Kundenseite (sc-3366), dazu der Abdeckungszähler
                reportEmbedConversion(parentReportConversion({ event: "interest_registered", transactionId: registrationId }))
            }
            history.push(URL_RESULTS.replace(":resultType", "interestRegistered"))
        } catch (error) {
            const rawErrorMessage = getGraphQLErrorMessage(error) || ""
            const errorCode = (error as { graphQLErrors?: Array<{ extensions?: { code?: string } }> })?.graphQLErrors?.[0]?.extensions?.code
            if (rawErrorMessage.includes("Access token not valid")) {
                history.push(URL_RESULTS.replace(":resultType", "loggedOut"))
            } else if (errorCode === "BAD_USER_INPUT" && rawErrorMessage) {
                // Fachliche Server-Meldung (bereits deutsch) anzeigen und auf der
                // Seite bleiben, damit der Nutzer die Eingabe korrigieren kann.
                message.error(rawErrorMessage, 6)
            } else {
                Sentry.captureException(error)
                history.push(URL_RESULTS.replace(":resultType", "unknown"))
            }
        }
    }

    // Submit-Button (Header + Footer teilen sich denselben Handler) — wie in BookingSummary.
    const submitButton = (
        <Button type='primary' block loading={submitting} data-testid='interest-submit' onClick={submit}>
            {t("interest.flow.submit", "Unverbindlich eintragen") as string}
        </Button>
    )

    const attendeeSection = (
        <div>
            <Title level={5} style={{ marginBottom: 4 }}>
                {t("interest.attendee.title", "Für wen bekundest du Interesse?")}
            </Title>
            {loadingBooker && !booker ? (
                <Spin />
            ) : (
                <Space direction='vertical' style={{ width: "100%" }}>
                    {attendees.length > 0 && (
                        <Radio.Group data-testid='interest-attendee-group' value={selectedAttendeeId} onChange={(e) => setSelectedAttendeeId(e.target.value)}>
                            <Space direction='vertical'>
                                {attendees.map((attendee) => (
                                    <Radio key={attendee.id} value={attendee.id}>
                                        {attendee.firstname} {attendee.lastname}
                                    </Radio>
                                ))}
                            </Space>
                        </Radio.Group>
                    )}
                    {showCreateAttendee && booker ? (
                        <CreateEditAttendee
                            booker={booker}
                            onCancel={() => setShowCreateAttendee(false)}
                            onOk={(data) => {
                                setShowCreateAttendee(false)
                                if (data?.id) setSelectedAttendeeId(data.id)
                                refetchBooker({ id: booker.id ?? "" })
                            }}
                        />
                    ) : (
                        <Button type='primary' ghost icon={<PlusCircleOutlined />} onClick={() => setShowCreateAttendee(true)}>
                            {t("interest.attendee.add", "Neuen Teilnehmer anlegen")}
                        </Button>
                    )}
                    {showError && !selectedAttendeeId && <Alert type='error' showIcon message={t("interest.attendee.required", "Bitte wähle einen Teilnehmer aus.")} />}
                </Space>
            )}
        </div>
    )

    const detailsSection = (
        <Space direction='vertical' size='large' style={{ width: "100%" }}>
            <div>
                <Paragraph style={{ marginBottom: 8 }}>
                    {t("interest.flow.intro", "Wähle den Teilnehmer und, falls vorhanden, passende Zeiten für deinen unverbindlichen Eintrag aus.")}
                </Paragraph>
                <Space direction='vertical' size={2}>
                    <Text strong>{list.courseType?.name}</Text>
                    {list.location?.name && <Text type='secondary'>{list.location.name}</Text>}
                    {list.description && <Paragraph style={{ marginBottom: 0 }}>{list.description}</Paragraph>}
                </Space>
            </div>

            {hasTimeSlots && <InterestTimeSlotSelect timeSlots={list.timeSlots ?? []} value={timeSlotValue} onChange={setTimeSlotValue} showError={showError} />}

            {/* `!== false` absichtlich defensiv: fehlt das Feld (alter Server /
                gestaffelter Rollout), bleibt das bisherige Verhalten sichtbar. */}
            {list.showNoteField !== false && (
                <div>
                    <Title level={5} style={{ marginBottom: 4 }}>
                        {t("interest.note.label", "Anmerkung / Wünsche")}
                    </Title>
                    <Input.TextArea
                        data-testid='interest-note'
                        value={note}
                        onChange={(e) => setNote(e.target.value)}
                        maxLength={NOTE_MAX_LENGTH}
                        showCount
                        rows={3}
                        placeholder={t("interest.note.placeholder", "Optional: Teil uns deine Wünsche mit (max. 250 Zeichen).")}
                    />
                </div>
            )}
        </Space>
    )

    // Layout im Look des Buchungs-Checkouts (BookingSummary): Card mit Header-Submit,
    // zweispaltige Row (links grau = Teilnehmer, rechts weiß = Angebot/Zeitfenster/Notiz),
    // Footer-Submit über die volle Breite. Ohne Zahlarten-/Zahlschritt.
    return (
        <Card
            data-testid='interest-registration'
            bodyStyle={{ padding: 0, borderTop: `3px solid ${blue[1]}` }}
            style={{ margin: margin.s }}
            title={
                <div style={{ display: "flex", flexWrap: "wrap", gap: 10, alignItems: "center", justifyContent: "space-between" }}>
                    <Space size='small' wrap>
                        <Tag color='blue'>{t("interest.badge", "Interessenten-Liste")}</Tag>
                        <span style={{ fontWeight: 600 }}>{list.courseType?.name}</span>
                    </Space>
                    {media !== "xs" && <div style={{ minWidth: 220 }}>{submitButton}</div>}
                </div>
            }
            actions={[<div style={{ padding: "0 10px" }}>{submitButton}</div>]}
        >
            <Row style={{ margin: 0 }}>
                <Col span={media === "xs" ? 24 : 10} style={{ background: "#eee", padding: media === "xs" ? margin.s : margin.std }}>
                    {attendeeSection}
                </Col>
                <Col span={media === "xs" ? 24 : 14} style={{ background: "#fff", padding: media === "xs" ? margin.s : margin.std }}>
                    {detailsSection}
                </Col>
            </Row>
        </Card>
    )
}

export default InterestRegistration
